import type {
  DocumentNode,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  TypedDocumentNode,
  Unmasked,
} from '@apollo/client';
import { NetworkStatus, isNetworkRequestSettled, useQuery } from '@apollo/client';
import { useEffect, useRef } from 'react';
import { logger } from '../../logger';
import { client } from '../client';
import { precacheOperation } from '../precache/precacheOperation';
import { getPrecacheOperationKey } from '../precache/getPrecacheOperationKey';

const DEFAULT_REFETCH_DELAY = 5000;

const refetchedOperations = new Set<string>();
const lastResponseHashes = new Map<string, string>();

// FNV-1a 32-bit hash constants (offset basis + prime).
const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

// FNV-1a over the JSON form of the response. Apollo selection-set order is stable, so JSON.stringify is byte-identical for unchanged responses. Hashing a ~10MB viewer payload runs in single-digit ms — orders of magnitude cheaper than the cache write it gates.
function hashJson(value: unknown): string {
  const s = JSON.stringify(value);
  if (s == null) return '';
  let h = FNV_OFFSET_BASIS;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, FNV_PRIME);
  }
  return (h >>> 0).toString(16) + ':' + s.length;
}

export const usePrecacheQuery = <
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryHookOptions<TData, TVariables>,
  precacheOptions?: {
    refetchDelay?: number;
    precacheEnabled?: boolean;
    onRefetch?: (newData: TData, oldData: TData | undefined) => void | Promise<void>;
  }
): QueryResult<TData, TVariables> => {
  // Track networkStatus across renders so the delayed refetch can see the live
  // state at fire time, not the stale value captured when the timer was scheduled.
  const networkStatusRef = useRef<NetworkStatus | undefined>(undefined);
  const refetchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  // Operation key of the currently scheduled (not-yet-fired) refetch, so that
  // cancelling the timeout can also un-mark the operation as refetched.
  const scheduledOperationKeyRef = useRef<string>();

  // Clear any pending precache refetch when the consumer unmounts. Otherwise the
  // delayed refetch fires against a torn-down component — leaking work in
  // production and, in tests, triggering React state updates outside act() after
  // the test has finished (a cascade of act() warnings across the suite).
  useEffect(
    () => () => {
      if (refetchTimeoutRef.current === undefined) return;
      clearTimeout(refetchTimeoutRef.current);
      refetchTimeoutRef.current = undefined;
      // The refetch never ran, so allow it to be scheduled again on a later
      // mount rather than leaving the operation permanently marked as refetched.
      if (scheduledOperationKeyRef.current !== undefined) {
        refetchedOperations.delete(scheduledOperationKeyRef.current);
        scheduledOperationKeyRef.current = undefined;
      }
    },
    []
  );

  const result = useQuery<TData, TVariables>(query, {
    ...options,
    context: {
      ...options?.context,
      // if there aren't any enabled precache flags, we can consider
      // the precache to be 'off', and not store anything there even
      // from "normal" query calls. This helps be more "zero-footprint",
      // but also our metrics check the cache for the data to mark
      // whether it was precached or not, so we don't want to add stuff
      // that could get picked up on a page refresh
      precachePolicy: precacheOptions?.precacheEnabled === true ? 'cache-first' : 'network-only',
    },
  });

  // once we have data, schedule a refetch to check for fresh data after 5 seconds
  useEffect(() => {
    // if we don't have data or an error, the query isn't done yet
    if (result.data == null && result.error == null) return;

    const beforeData = result.data;

    // Give 5 seconds to the Viewer to load, after that we can attempt to
    // fetch fresh data from the server and in case refresh the Viewer to
    // reflect any changes.
    // We want to delay this operation because the fetch operation can affect
    // the performance of the Viewer.
    // We can consider this a good trade-off to ensure users don't see stale
    // data for too long.
    const operationKey = getPrecacheOperationKey(query, options?.variables);

    // Seed the hash from the initial response so the first delayed refetch can
    // short-circuit when the server returns the same payload.
    if (!lastResponseHashes.has(operationKey)) {
      lastResponseHashes.set(operationKey, hashJson(beforeData));
    }

    // If we already refetched this operation, we don't need to do it again
    if (refetchedOperations.has(operationKey)) {
      logger.debug('[usePrecacheQuery] operation already refetched, skipping refetch');
      return;
    }

    // Cancel any earlier pending refetch so only one is in flight per hook
    // instance (e.g. if useQuery runs again for changed variables). The
    // cancelled refetch never ran, so un-mark its operation too — mirroring the
    // unmount cleanup. Otherwise the old key is orphaned in refetchedOperations
    // forever and silently blocks rescheduling it on a later mount.
    if (refetchTimeoutRef.current !== undefined) {
      clearTimeout(refetchTimeoutRef.current);
      refetchTimeoutRef.current = undefined;
      if (scheduledOperationKeyRef.current !== undefined) {
        refetchedOperations.delete(scheduledOperationKeyRef.current);
        scheduledOperationKeyRef.current = undefined;
      }
    }

    // Mark this operation as refetched so we don't do it again in the future
    refetchedOperations.add(operationKey);
    scheduledOperationKeyRef.current = operationKey;

    logger.debug('[usePrecacheQuery] refetching operation scheduled');
    refetchTimeoutRef.current = setTimeout(async () => {
      refetchTimeoutRef.current = undefined;
      scheduledOperationKeyRef.current = undefined;

      // Bail if a fetch is already in flight (manual refetch, refetchOnReconnect, etc.) — Apollo will write the fresh response itself.
      if (networkStatusRef.current != null && !isNetworkRequestSettled(networkStatusRef.current)) {
        logger.debug('[usePrecacheQuery] another fetch in flight; skipping delayed refetch');
        return;
      }

      logger.debug('[usePrecacheQuery] refetching operation (precache)');
      const data = await precacheOperation(query, options?.variables, {
        refetch: true,
        context: options?.context,
      });
      if (data == null) return;

      // Skip the Apollo cache write when the response is byte-identical to the last one we wrote. processSelectionSet/processFieldValue on the viewer query is ~2s of main-thread work — avoiding it entirely is the win.
      const hash = hashJson(data);
      if (lastResponseHashes.get(operationKey) === hash) {
        logger.debug('[usePrecacheQuery] refetch unchanged, skipping Apollo cache write');
        return;
      }
      lastResponseHashes.set(operationKey, hash);

      logger.debug('[usePrecacheQuery] writing refetched data to Apollo cache');
      client.writeQuery({
        query,
        variables: options?.variables,
        data: data as Unmasked<TData>,
      });

      // Notify consumers of the fresh data so they can react to what changed
      // (e.g. logRefetchDiff). Only runs when the payload actually changed.
      try {
        await precacheOptions?.onRefetch?.(data as TData, beforeData);
      } catch (e) {
        logger.error('[usePrecacheQuery] onRefetch callback error', { error: e });
      }
    }, precacheOptions?.refetchDelay ?? DEFAULT_REFETCH_DELAY);
  }, [query, options, precacheOptions, result.data, result.error]);

  networkStatusRef.current = result.networkStatus;

  return result;
};

// Test-only: reset module state between cases so module-level dedup doesn't leak across tests.
export const __resetUsePrecacheQueryForTests = (): void => {
  refetchedOperations.clear();
  lastResponseHashes.clear();
};
